import React, { useEffect, useState } from 'react';
import MetricInput from './MetricInput';
import { stepsForTool } from './toolRefs';

const API_BASE = process.env.REACT_APP_API_URL;

function genId() { return 't' + Math.random().toString(36).slice(2, 9); }

// Tools & Equipment editor for an Assembly Instruction. Each tool can LINK to a
// record in the Equipment or Jig registry — on link it auto-fills the name, doc
// number, calibration flag and the registry item's key metric. The metric type
// is chosen from the tool-metric taxonomy (torque / resolution / storage / …).
// readOnly renders a compact display (used in the Detail read view).
export default function ToolEditor({ tools, onChange, getToken, accent = '#2dd4bf', readOnly = false, steps = [] }) {
  const [metricTypes, setMetricTypes] = useState([]);
  const [registry, setRegistry] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [mt, eq, jg] = await Promise.all([
          window.fetch(`${API_BASE}/quality/tool-metric-types`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
          window.fetch(`${API_BASE}/quality/equipment`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
          window.fetch(`${API_BASE}/quality/jigs`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
        ]);
        if (cancelled) return;
        if (mt.ok) setMetricTypes(mt.metricTypes || []);
        const eqItems = (eq.items || []).map(x => ({ ...x, refType: 'equipment' }));
        const jgItems = (jg.jigs || []).map(x => ({ ...x, refType: 'jig' }));
        setRegistry([...eqItems, ...jgItems]);
      } catch { /* leave empty — manual entry still works */ }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const metricLabel = (key) => (metricTypes.find(t => t.key === key) || {}).label || key;
  const refsFor = (tool) => (tool.id ? stepsForTool(steps, tool.id) : []);
  const refNote = (tool) => {
    const r = refsFor(tool);
    if (!r.length) return null;
    return <span style={{ fontSize: 11, color: accent }}>↳ used in step{r.length > 1 ? 's' : ''} {r.join(', ')}</span>;
  };
  const list = tools || [];
  const setTool = (i, patch) => onChange(list.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const removeTool = (i) => onChange(list.filter((_, j) => j !== i));
  const addTool = () => onChange([...list, { id: genId(), name: '', refType: '', refId: '', documentNumber: '',
    calibrationRequired: false, metricType: '', metricValue: '', metricUnit: '' }]);

  const linkRegistry = (i, val) => {
    if (!val) { setTool(i, { refType: '', refId: '', documentNumber: '' }); return; }
    const [type, id] = val.split('::');
    const item = registry.find(r => r.refType === type && String(r._id) === id);
    if (!item) return;
    const patch = { refType: type, refId: String(item._id), documentNumber: item.documentNumber || '', name: item.name || '' };
    if (type === 'equipment' && item.calibrationRequired) patch.calibrationRequired = true;
    const km = item.keyMetric;
    if (km && km.metricType) { patch.metricType = km.metricType; patch.metricValue = km.metricValue || ''; patch.metricUnit = km.metricUnit || ''; }
    setTool(i, patch);
  };

  const inp = { background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)',
    borderRadius: 6, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const badge = (text, color) => <span style={{ fontSize: 10, fontWeight: 700, color, background: color + '20', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{text}</span>;

  if (readOnly) {
    if (!list.length) return <div style={{ fontSize: 13, color: 'var(--ni-text-muted)' }}>No tools listed.</div>;
    return (
      <div>
        {list.map((t, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--ni-border-soft)', fontSize: 13, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{t.name || '—'}</span>
            {t.documentNumber && <span style={{ fontFamily: 'monospace', fontSize: 11, color: accent }}>🔗 {t.documentNumber}</span>}
            {t.metricType && <span style={{ color: 'var(--ni-text-muted)' }}>{metricLabel(t.metricType)}: <strong style={{ color: 'var(--ni-text)' }}>{t.metricValue} {t.metricUnit}</strong></span>}
            {!t.metricType && t.torqueSpec && <span style={{ color: 'var(--ni-text-muted)' }}>Torque: <strong style={{ color: 'var(--ni-text)' }}>{t.torqueSpec}</strong></span>}
            {refNote(t)}
            <span style={{ marginLeft: 'auto' }}>{t.calibrationRequired && badge('CALIBRATED', '#f59e0b')}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {list.map((t, i) => (
        <div key={i} style={{ border: '1px solid var(--ni-border-soft)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input value={t.name || ''} onChange={e => setTool(i, { name: e.target.value })} placeholder="Tool / equipment name" style={inp} />
            <select value={t.refType ? `${t.refType}::${t.refId}` : ''} onChange={e => linkRegistry(i, e.target.value)} style={inp}>
              <option value="">Link registry item…</option>
              <optgroup label="Equipment">
                {registry.filter(r => r.refType === 'equipment').map(r => <option key={'e' + r._id} value={`equipment::${r._id}`}>{r.documentNumber} — {r.name}</option>)}
              </optgroup>
              <optgroup label="Jigs">
                {registry.filter(r => r.refType === 'jig').map(r => <option key={'j' + r._id} value={`jig::${r._id}`}>{r.documentNumber} — {r.name}</option>)}
              </optgroup>
            </select>
            <button type="button" onClick={() => removeTool(i)} style={{ background: 'none', border: 'none', color: '#EC2124', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
            <MetricInput value={{ metricType: t.metricType, metricValue: t.metricValue, metricUnit: t.metricUnit }}
              onChange={m => setTool(i, m)} metricTypes={metricTypes} getToken={getToken} accent={accent} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!t.calibrationRequired} onChange={e => setTool(i, { calibrationRequired: e.target.checked })} /> Calibrated
            </label>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
            {t.documentNumber && <span style={{ fontSize: 11, color: 'var(--ni-text-muted)' }}>🔗 Linked to {t.refType} {t.documentNumber}</span>}
            {refNote(t)}
          </div>
        </div>
      ))}
      <button type="button" onClick={addTool}
        style={{ background: accent + '22', color: accent, border: `1px solid ${accent}44`, borderRadius: 6, padding: '4px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
        + Tool
      </button>
    </div>
  );
}
